import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import Map from "ol/Map.js";
import View from "ol/View.js";
import TileLayer from "ol/layer/Tile.js";
import TileWMS from "ol/source/TileWMS.js";
import OSM from "ol/source/OSM";
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Draw, Select } from "ol/interaction";
import Overlay from 'ol/Overlay';
import { WKT } from "ol/format";
import GeoJSON from 'ol/format/GeoJSON';
import { defaults as defaultControls } from 'ol/control';
import { Style, Fill, Stroke, Circle, Text } from 'ol/style';
import { useGeographic } from 'ol/proj';
import { createEmpty, extend as extendExtent, isEmpty as isEmptyExtent } from 'ol/extent';

import "ol/ol.css";

import { useGeo } from '../context/GeoContext';
import { useFormContext } from '../context/FormContext';
import useBarrioLayers from '../hooks/useBarrioLayers';
import usePointInTarget from '../hooks/usePointInTarget';
import { reverseGeocodePoint } from '../services/nominatimClient';
import PanelCapas from './map/PanelCapas';
import PanelLeyenda from './map/PanelLeyenda';
import PanelDebug from './map/PanelDebug';

async function reverseGeocodeNominatim(lat, lng) {
    const geo = await reverseGeocodePoint(lat, lng);
    return geo?.barrio || null;
}

const MapView = forwardRef(({
    onClose, onFeatureDataChange, initialFeatures = [],
    pendingBeneficiarioId = null,
    pendingBeneficiarioNombre = '',
    onBeneficiaryPointPlaced = null,
    onOutsideTargetConfirm = null,
    onSetOtroTipo = null,
    onSetOtroNombre = null,
    beneficiaryFeatures = [],
    zoomToBeneficiaryId = null,
    highlightBeneficiaryId = null,
    zoomToCoords = null,
    disableDrawing = false,
    geoEditMode = false,
    renabapIds = '',
    rppvapCodigos = '',
    noAutoZoom = false,
    onBarriosReady = null
}, ref) => {
    useGeographic();
    const { formA } = useFormContext();
    const partido = formA?.datosGenerales?.partido;

    const { drawnFeatures, handleFeaturesUpdate, deleteFeature } = useGeo();

    const mapRef = useRef(null);
    const [map, setMap] = useState(null);
    const drawInteractionRef = useRef(null);
    const selectInteractionRef = useRef(null);
    const beneficiaryVectorSource = useRef(new VectorSource());
    const vectorSource = useRef(new VectorSource());
    const wktFormat = useRef(new WKT());
    const arbaLayerRef = useRef(null);
    const tooltipOverlayRef = useRef(null);
    const barrioHoverSource = useRef(new VectorSource());
    const featureData = drawnFeatures;

    const noAutoZoomRef = useRef(false);
    noAutoZoomRef.current = noAutoZoom || !!(zoomToBeneficiaryId || zoomToCoords);
    const zoomToCoordsRef = useRef(null);
    const pendingBeneficiarioIdRef = useRef(pendingBeneficiarioId);
    pendingBeneficiarioIdRef.current = pendingBeneficiarioId;

    const { targetFeaturesRef, allBarriosFeaturesRef, barrioCounts, layersLoaded, layersRef: barrioLayersRef, toggleLayer: toggleBarrioLayer, hasRenabapTargets, hasRppvapTargets, hasRenabapData, hasRppvapData } = useBarrioLayers({
        map, partido, renabapIds, rppvapCodigos, noAutoZoomRef,
    });

    const { isInsideTarget, findTargetBarrio, findBarrio, confirmIfOutside } = usePointInTarget(targetFeaturesRef, allBarriosFeaturesRef, onOutsideTargetConfirm);

    const checkGeom = (geom, c) => {
        if (typeof geom.containsXY === 'function') return geom.containsXY(c[0], c[1]);
        if (geom.getPolygons) return geom.getPolygons().some(p => checkGeom(p, c));
        return false;
    };

    const [showNonTargetOverlay, setShowNonTargetOverlay] = useState(false);
    const showNonTargetOverlayRef = useRef(false);

    const handleShowNonTargetOverlay = (checked) => {
        showNonTargetOverlayRef.current = checked;
        setShowNonTargetOverlay(checked);
    };
    const [selectedDrawType, setSelectedDrawType] = useState('none');
    const [selectedWKTIndex, setSelectedWKTIndex] = useState(null);
    const [highlightSource, setHighlightSource] = useState(new VectorSource());
    const [pendingPoint, setPendingPoint] = useState(null);
    const [outsideTarget, setOutsideTarget] = useState(null);
    const [debugInfo, setDebugInfo] = useState(null);
    const pendingFeatureRef = useRef(null);
    const layersRef = useRef({});

    const [layerVisibility, setLayerVisibility] = useState({
        partido: true, renabap: true, rppvap: true, barriosNoTarget: true,
        renabapTarget: true, rppvapTarget: true,
        beneficiarios: true, arba: false,
    });

    useImperativeHandle(ref, () => ({
        layersLoaded,
        clearAllFeatures: () => {
            if (vectorSource.current) {
                vectorSource.current.clear();
            }
        },
        checkTargetForWkt: (wkt) => {
            const match = wkt && wkt.match(/POINT\s*\(([-\d.]+)\s+([-\d.]+)\)/);
            if (!match) return { inTarget: true, nonTargets: [] };
            const coords = [parseFloat(match[1]), parseFloat(match[2])];
            const inTarget = targetFeaturesRef.current.some(tf => {
                const geom = tf.getGeometry();
                return geom && checkGeom(geom, coords);
            });
            const nonTargets = [];
            if (!inTarget) {
                const seen = new Set();
                for (const bf of allBarriosFeaturesRef.current) {
                    const geom = bf.getGeometry();
                    if (geom && checkGeom(geom, coords)) {
                        const key = `${bf.get('tipo')}_${bf.get('id')}`;
                        if (seen.has(key)) continue;
                        seen.add(key);
                        nonTargets.push({ id: bf.get('id'), nombre: bf.get('nombre'), tipo: bf.get('tipo') });
                    }
                }
            }
            return { inTarget, nonTargets };
        },
        // Devuelve TODOS los barrios (target o no) que contienen el punto
        findBarriosForWkt: (wkt) => {
            const match = wkt && wkt.match(/POINT\s*\(([-\d.]+)\s+([-\d.]+)\)/);
            if (!match) return [];
            const coords = [parseFloat(match[1]), parseFloat(match[2])];
            const result = [];
            const seen = new Set();
            for (const bf of allBarriosFeaturesRef.current) {
                const geom = bf.getGeometry();
                if (geom && checkGeom(geom, coords)) {
                    const key = `${bf.get('tipo')}_${bf.get('id')}`;
                    if (seen.has(key)) continue;
                    seen.add(key);
                    result.push({ id: bf.get('id'), nombre: bf.get('nombre'), tipo: bf.get('tipo') });
                }
            }
            return result;
        },
        highlightBeneficiary: (id) => {
            highlightSource.clear();
            if (id) {
                const feat = beneficiaryVectorSource.current.getFeatures().find(f => f.getId() === id);
                if (feat) highlightSource.addFeature(feat);
            }
        },
        zoomToTargets: () => {
            const targets = targetFeaturesRef.current.filter(tf => tf.getGeometry());
            if (!targets.length) return;
            const extent = createEmpty();
            targets.forEach(tf => extendExtent(extent, tf.getGeometry().getExtent()));
            if (isEmptyExtent(extent)) return;
            map.getView().fit(extent, {
                padding: [80, 80, 80, 80],
                duration: 1000,
                maxZoom: 16,
            });
        },
        addOutsideTargets: (wkt) => {
            const match = wkt && wkt.match(/POINT\s*\(([-\d.]+)\s+([-\d.]+)\)/);
            if (!match) return;
            const coords = [parseFloat(match[1]), parseFloat(match[2])];
            if (!onOutsideTargetConfirm) return;
            const seen = new Set();
            let added = false;
            let msg = '';
            for (const bf of allBarriosFeaturesRef.current) {
                const geom = bf.getGeometry();
                if (geom && checkGeom(geom, coords)) {
                    const key = `${bf.get('tipo')}_${bf.get('id')}`;
                    if (seen.has(key)) continue;
                    seen.add(key);
                    onOutsideTargetConfirm({ id: bf.get('id'), nombre: bf.get('nombre'), tipo: bf.get('tipo') });
                    added = true;
                    msg += `\n  • ${bf.get('nombre')} (${bf.get('tipo')})`;
                }
            }
            if (added) {
                setDebugInfo(prev => (prev || '') + `\n[geocoding] Barrios agregados:${msg}`);
            }
        },
    }));

    useEffect(() => {
        if (onFeatureDataChange) {
            onFeatureDataChange(drawnFeatures);
        }
    }, [drawnFeatures, onFeatureDataChange]);

    useEffect(() => {
        return () => {
            if (vectorSource.current) vectorSource.current.clear();
            if (selectInteractionRef.current) map?.removeInteraction(selectInteractionRef.current);
            if (drawInteractionRef.current) map?.removeInteraction(drawInteractionRef.current);
        };
    }, []);

    const defaultFeatureStyle = new Style({
        fill: new Fill({ color: 'rgba(255, 255, 255, 0.2)' }),
        stroke: new Stroke({ color: '#ffcc33', width: 2 }),
        image: new Circle({ radius: 7, fill: new Fill({ color: '#ffcc33' }) }),
    });

    const vectorLayerRef = useRef(
        new VectorLayer({
            source: vectorSource.current,
            style: (feature) => {
                const labelText = feature.get('display_label');
                return new Style({
                    fill: new Fill({ color: 'rgba(255, 255, 255, 0.2)' }),
                    stroke: new Stroke({ color: '#ffcc33', width: 2 }),
                    image: new Circle({ radius: 7, fill: new Fill({ color: '#ffcc33' }) }),
                    text: new Text({
                        text: labelText ? String(labelText) : '',
                        fill: new Fill({ color: 'red' }),
                        stroke: new Stroke({ color: '#fff', width: 3 }),
                        font: '14px sans-serif',
                        offsetY: -20,
                        textAlign: 'center',
                        textBaseline: 'bottom',
                        placement: 'point',
                    }),
                });
            },
        })
    );

    useEffect(() => {
        const highlightLayer = new VectorLayer({
            source: highlightSource,
            zIndex: 6,
            style: (feature) => {
                const geomType = feature.getGeometry()?.getType();
                if (geomType === 'Point') {
                    return new Style({
                        image: new Circle({
                            radius: 14,
                            fill: new Fill({ color: 'rgba(255, 87, 34, 0.15)' }),
                            stroke: new Stroke({ color: '#ff5722', width: 2 }),
                        }),
                    });
                }
                return new Style({
                    stroke: new Stroke({ color: '#007bff', width: 3 }),
                    fill: new Fill({ color: 'rgba(0, 123, 255, 0.5)' }),
                });
            },
        });

        const beneficiaryLayer = new VectorLayer({
            source: beneficiaryVectorSource.current,
            zIndex: 5,
            style: (feature) => {
                const label = feature.get('label');
                const pid = pendingBeneficiarioIdRef.current;
                const isActive = pid && String(feature.getId()) === String(pid);
                return new Style({
                    image: new Circle({
                        radius: isActive ? 12 : 6,
                        fill: new Fill({ color: isActive ? '#ff5722' : '#6B8EAB' }),
                        stroke: new Stroke({ color: isActive ? '#fff' : '#8DA5B3', width: isActive ? 3 : 1.5 }),
                    }),
                    text: label ? new Text({
                        text: label,
                        fill: new Fill({ color: '#333' }),
                        stroke: new Stroke({ color: '#fff', width: 3 }),
                        font: '12px sans-serif',
                        offsetY: -16,
                    }) : undefined,
                });
            },
    });
    layersRef.current.beneficiarios = beneficiaryLayer;

    const barrioHoverLayer = new VectorLayer({
        source: barrioHoverSource.current,
        style: (feature) => {
            const tipo = feature.get('tipo');
            const color = tipo === 'RENABAP' ? '#F9A825' : tipo === 'RPPVAP' ? '#E53935' : '#666';
            return new Style({
                stroke: new Stroke({ color, width: 4 }),
                fill: new Fill({ color: 'transparent' }),
            });
        },
        zIndex: 10,
    });

        const arbaWmsLayer = new TileLayer({
            source: new TileWMS({
                url: 'https://geo.arba.gov.ar/geoserver/idera/wms',
                params: {
                    'LAYERS': 'idera:Parcela',
                    'TILED': true,
                    'FORMAT': 'image/png',
                    'TRANSPARENT': true,
                    'VERSION': '1.1.1',
                    'SRS': 'EPSG:3857'
                },
                attributions: '© ARBA',
                crossOrigin: 'anonymous'
            }),
            visible: false,
            opacity: 0.8,
        });
        arbaLayerRef.current = arbaWmsLayer;

        const initialMap = new Map({
            target: mapRef.current,
            layers: [
                new TileLayer({ source: new OSM() }),
                arbaWmsLayer,
                beneficiaryLayer,
                barrioHoverLayer,
                vectorLayerRef.current,
                highlightLayer,
            ],
            view: new View({
                center: [-60.50, -36.67],
                zoom: 5,
            }),
            controls: defaultControls({ attribution: false, zoom: true }),
        });

        setMap(initialMap);

        const selectInteraction = new Select({
            layers: [vectorLayerRef.current],
            style: null,
        });

        const handleSelect = (event) => {
            handleFeatureSelectOnMap(event.selected);
        };

        selectInteraction.on('select', handleSelect);
        selectInteractionRef.current = selectInteraction;
        initialMap.addInteraction(selectInteraction);

        const tooltipEl = document.createElement('div');
        tooltipEl.className = 'pointer-events-none bg-black bg-opacity-75 text-white text-xs rounded px-2 py-1 whitespace-nowrap';
        const tooltipOverlay = new Overlay({
            element: tooltipEl,
            offset: [0, -12],
            positioning: 'bottom-center',
        });
        initialMap.addOverlay(tooltipOverlay);
        tooltipOverlayRef.current = tooltipOverlay;

        const pointerMoveHandler = (evt) => {
            const targetLayers = new Set();
            const bl = barrioLayersRef.current;
            if (bl.renabapTarget) targetLayers.add(bl.renabapTarget);
            if (bl.rppvapTarget) targetLayers.add(bl.rppvapTarget);

            const seen = new Set();
            const hits = [];
            initialMap.forEachFeatureAtPixel(evt.pixel, (f, layer) => {
                if (!f.get('tipo')) return;
                if (!targetLayers.has(layer) && !showNonTargetOverlayRef.current) return;
                const key = `${f.get('tipo')}_${f.get('id')}`;
                if (seen.has(key)) return;
                seen.add(key);
                hits.push(f);
            });
            barrioHoverSource.current.clear();
            if (hits.length > 0) {
                const sorted = [...hits].sort((a, b) => {
                    const ta = a.get('tipo'), tb = b.get('tipo');
                    if (ta === 'RENABAP' && tb !== 'RENABAP') return -1;
                    if (ta !== 'RENABAP' && tb === 'RENABAP') return 1;
                    return 0;
                });
                const lines = sorted.map(h => `${h.get('nombre')} (${h.get('id')}) - ${h.get('tipo')}`);
                tooltipEl.innerHTML = lines.join('<br>');
                tooltipOverlay.setPosition(evt.coordinate);
                sorted.forEach(h => barrioHoverSource.current.addFeature(h.clone()));
                initialMap.getTargetElement().style.cursor = 'pointer';
            } else {
                tooltipOverlay.setPosition(undefined);
                if (!pendingBeneficiarioId) {
                    initialMap.getTargetElement().style.cursor = '';
                }
            }
        };

        initialMap.on('pointermove', pointerMoveHandler);

        return () => {
            initialMap.un('pointermove', pointerMoveHandler);
            initialMap.removeOverlay(tooltipOverlay);
            initialMap.setTarget(undefined);
            if (selectInteractionRef.current) {
                initialMap.removeInteraction(selectInteractionRef.current);
                selectInteractionRef.current.un('select', handleSelect);
            }
            if (drawInteractionRef.current) {
                initialMap.removeInteraction(drawInteractionRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!map) return;
        if (drawnFeatures.length > 0) {
            const features = [];
            const wktFmt = new WKT();
            drawnFeatures.forEach(item => {
                try {
                    if (item.wkt) {
                        const feature = wktFmt.readFeature(item.wkt, {
                            dataProjection: 'EPSG:4326',
                            featureProjection: 'EPSG:4326',
                        });
                        feature.setId(item.id);
                        feature.set('display_label', item.feature?.get('display_label') || '');
                        features.push(feature);
                    }
                } catch (error) {
                    console.error('Error loading feature:', error);
                }
            });
            if (features.length > 0) {
                vectorSource.current.clear();
                vectorSource.current.addFeatures(features);
            }
        }
    }, [map]);

    useEffect(() => {
        if (!map) return;
        beneficiaryVectorSource.current.clear();
        if (beneficiaryFeatures.length > 0) {
            const features = beneficiaryFeatures
                .filter(b => b.wkt)
                .map(b => {
                    try {
                        const feature = wktFormat.current.readFeature(b.wkt, {
                            dataProjection: 'EPSG:4326',
                            featureProjection: 'EPSG:4326',
                        });
                        feature.set('label', b.label);
                        feature.setId(b.id);
                        return feature;
                    } catch (e) {
                        console.warn('Error parsing beneficiary WKT:', e);
                        return null;
                    }
                })
                .filter(Boolean);
            features.forEach(f => beneficiaryVectorSource.current.addFeature(f));
        }
        if (zoomToBeneficiaryId && !noAutoZoom) {
            const allFeatures = beneficiaryVectorSource.current.getFeatures();
            const target = allFeatures.find(f => f.getId() === zoomToBeneficiaryId);
            if (target && target.getGeometry()) {
                map.getView().fit(target.getGeometry().getExtent(), {
                    padding: [100, 100, 100, 100],
                    duration: 1000,
                    maxZoom: 17,
                });
            }
        }
    }, [map, beneficiaryFeatures, zoomToBeneficiaryId, noAutoZoom]);

    // Resaltar beneficiario en el mapa
    useEffect(() => {
        if (!map) return;
        highlightSource.clear();
        if (highlightBeneficiaryId) {
            const feat = beneficiaryVectorSource.current.getFeatures().find(f => f.getId() === highlightBeneficiaryId);
            if (feat) highlightSource.addFeature(feat);
        }
    }, [map, highlightBeneficiaryId, beneficiaryFeatures]);

    // Zoom automático al punto geocodificado (desde Modal2 tras "Completar datos primero")
    useEffect(() => {
        if (!map || !zoomToCoords) return;
        const { lat, lng } = zoomToCoords;
        const pointGeom = new WKT().readGeometry(`POINT (${lng} ${lat})`, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:4326',
        });
        if (pointGeom) {
            map.getView().fit(pointGeom.getExtent(), {
                padding: [100, 100, 100, 100],
                duration: 1000,
                maxZoom: 17,
            });
        }
    }, [map, zoomToCoords]);

    // Notificar a Modal2 cuando los barrios terminan de cargar
    useEffect(() => {
        if (layersLoaded && onBarriosReady) {
            onBarriosReady(true);
        }
    }, [layersLoaded, onBarriosReady]);

    useEffect(() => {
        // Clear any pending point when switching context (beneficiarioId changes)
        if (pendingPoint) {
            if (pendingFeatureRef.current) {
                beneficiaryVectorSource.current.removeFeature(pendingFeatureRef.current);
            }
            setPendingPoint(null);
            setOutsideTarget(null);
            pendingFeatureRef.current = null;
        }
        if (pendingBeneficiarioId && map && !disableDrawing) {
            addDrawInteraction('Point');
            map.getTargetElement().style.cursor = 'crosshair';
        } else if ((!pendingBeneficiarioId || disableDrawing) && drawInteractionRef.current) {
            if (map) map.removeInteraction(drawInteractionRef.current);
            drawInteractionRef.current = null;
            if (map) map.getTargetElement().style.cursor = '';
        }
        // Forzar refresco de estilo de los puntos de beneficiarios para que se aplique isDimmed
        beneficiaryVectorSource.current.changed();
    }, [pendingBeneficiarioId, map, disableDrawing]);

    const addDrawInteraction = (type) => {
        setSelectedDrawType(type);
        if (!map) return;

        if (selectInteractionRef.current) {
            selectInteractionRef.current.setActive(false);
        }

        if (drawInteractionRef.current) {
            map.removeInteraction(drawInteractionRef.current);
            drawInteractionRef.current = null;
        }

        if (type !== "none") {
            const drawInteraction = new Draw({
                source: vectorSource.current,
                type: type,
            });

            drawInteraction.on("drawend", (evt) => {
                const feature = evt.feature;
                const wktFmt = new WKT();
                const uuid = crypto.randomUUID();

                feature.setId(pendingBeneficiarioIdRef.current || uuid);
                feature.set('display_label', '');
                feature.changed();
                vectorSource.current.changed();

                const featureWKT = wktFmt.writeFeature(feature);

                const newFeatureDataItem = {
                    id: uuid,
                    wkt: featureWKT,
                    feature: feature,
                    ol_uid: feature.ol_uid,
                };

                if (onBeneficiaryPointPlaced) {
                    vectorSource.current.removeFeature(feature);
                    if (pendingBeneficiarioId) {
                        const oldFeat = beneficiaryVectorSource.current.getFeatures().find(f => f.getId() === pendingBeneficiarioId);
                        if (oldFeat) beneficiaryVectorSource.current.removeFeature(oldFeat);
                        feature.set('label', pendingBeneficiarioNombre);
                    }
                    beneficiaryVectorSource.current.addFeature(feature);
                    if (drawInteractionRef.current) {
                        map.removeInteraction(drawInteractionRef.current);
                        drawInteractionRef.current = null;
                    }
                    if (selectInteractionRef.current) {
                        selectInteractionRef.current.setActive(true);
                    }
                    const coords = feature.getGeometry().getCoordinates();
                    if (pendingBeneficiarioId) {
                        // Flujo unificado: al dibujar el punto se delega la confirmación al
                        // overlay de búsqueda por dirección (Modal2). No se muestra la doble
                        // confirmación de MapView ("¿Confirmar ubicación?").
                        onBeneficiaryPointPlaced(featureWKT);
                        pendingFeatureRef.current = null;
                        // Permitir reubicar haciendo clic de nuevo: mantener la interacción
                        // de dibujo activa (drawend la removió arriba).
                        if (!disableDrawing) {
                            addDrawInteraction('Point');
                        }
                    } else {
                        setPendingPoint({ wkt: featureWKT, feature, lng: coords[0], lat: coords[1] });
                        pendingFeatureRef.current = feature;
                    }

                    // DEBUG
                    let dbg = `Targets: ${barrioCounts.targets} | Total: ${barrioCounts.total}`;
                    dbg += ` | RENABAP: ${barrioCounts.renabap ?? '?'} | RPPVAP: ${barrioCounts.rppvap ?? '?'}`;
                    const targetsHere = [];
                    const nonTargetsHere = [];
                    const seenTarget = new Set();
                    for (const tf of targetFeaturesRef.current) {
                        const geom = tf.getGeometry();
                        if (geom && checkGeom(geom, coords)) {
                            const key = `${tf.get('tipo')}_${tf.get('id')}`;
                            if (seenTarget.has(key)) continue;
                            seenTarget.add(key);
                            targetsHere.push(tf);
                        }
                    }
                    const seenNon = new Set();
                    for (const bf of allBarriosFeaturesRef.current) {
                        const geom = bf.getGeometry();
                        if (geom && checkGeom(geom, coords)) {
                            const key = `${bf.get('tipo')}_${bf.get('id')}`;
                            if (seenNon.has(key)) continue;
                            seenNon.add(key);
                            if (!targetsHere.some(t => t.get('id') === bf.get('id') && t.get('tipo') === bf.get('tipo'))) {
                                nonTargetsHere.push(bf);
                            }
                        }
                    }
                    if (targetsHere.length > 0) {
                        dbg += targetsHere.map(tf => `\n✅ TARGET: ${tf.get('nombre')} (${tf.get('tipo')})`).join('');
                    }
                    if (nonTargetsHere.length > 0) {
                        dbg += nonTargetsHere.map(bf => `\n⚠️ NO-TARGET: ${bf.get('nombre')} (${bf.get('tipo')})`).join('');
                    }
                    if (targetsHere.length === 0 && nonTargetsHere.length === 0) {
                        dbg += `\n❌ FUERA DE TODO BARRIO`;
                    }
                    if (nonTargetsHere.length > 0) {
                        setOutsideTarget({ inBarrios: nonTargetsHere });
                    } else if (targetsHere.length === 0) {
                        setOutsideTarget({ inBarrio: null });
                    } else {
                        setOutsideTarget(null);
                    }
                    dbg += `\n\nCoord: ${coords[0].toFixed(6)}, ${coords[1].toFixed(6)}`;
                    setDebugInfo(dbg);
                } else {
                    handleFeaturesUpdate(newFeatureDataItem);
                }

                setSelectedWKTIndex(null);
                highlightSource.clear();

                if (selectInteractionRef.current) {
                    selectInteractionRef.current.setActive(true);
                }
            });

            map.addInteraction(drawInteraction);
            drawInteractionRef.current = drawInteraction;
        } else {
            if (selectInteractionRef.current) {
                selectInteractionRef.current.setActive(true);
            }
        }
    };

    const handleConfirmPoint = () => {
        if (!pendingPoint || !onBeneficiaryPointPlaced) return;
        const coords = [pendingPoint.lng, pendingPoint.lat];

        if (outsideTarget) {
            if (onOutsideTargetConfirm) {
                const seen = new Set();
                const added = [];
                for (const bf of allBarriosFeaturesRef.current) {
                    const geom = bf.getGeometry();
                    if (geom && checkGeom(geom, coords)) {
                        const key = `${bf.get('tipo')}_${bf.get('id')}`;
                        if (seen.has(key)) continue;
                        seen.add(key);
                        const barrio = { id: bf.get('id'), nombre: bf.get('nombre'), tipo: bf.get('tipo') };
                        onOutsideTargetConfirm(barrio);
                        added.push(barrio);
                    }
                }
                if (added.length > 0) {
                    const msg = added.map(b => `  • ${b.nombre} (${b.tipo})`).join('\n');
                    alert(`Se agregaron los siguientes barrios como seleccionados:\n${msg}`);
                    setDebugInfo(prev => (prev || '') + `\n[CONFIRMAR] Barrios agregados:\n${msg}`);
                }
            }
        } else {
            const result = confirmIfOutside(coords);
            if (!result.confirmed) return;
            if (!result.barrio) {
                if (onSetOtroTipo) onSetOtroTipo();
                if (onSetOtroNombre) {
                    reverseGeocodeNominatim(pendingPoint.lat, pendingPoint.lng).then(nombre => {
                        if (nombre) {
                            onSetOtroNombre(nombre);
                            alert(`Se agregó "${nombre}" como barrio "Otro".`);
                        } else {
                            alert('No se pudo obtener el nombre del barrio desde OpenStreetMap.');
                        }
                    });
                }
            }
            setDebugInfo(prev => (prev || '') + `\n[CONFIRMAR] ${result.barrio ? 'Dentro de target: ' + result.barrio.nombre + ' (' + result.barrio.tipo + ')' : 'Fuera de todo barrio'}`);
        }

        // Remove old feature before saving the new one
        if (pendingBeneficiarioId) {
            const oldFeat = beneficiaryVectorSource.current.getFeatures().find(f => f.getId() === pendingBeneficiarioId);
            if (oldFeat) beneficiaryVectorSource.current.removeFeature(oldFeat);
        }
        onBeneficiaryPointPlaced(pendingPoint.wkt);
        setPendingPoint(null);
        setOutsideTarget(null);
        pendingFeatureRef.current = null;
    };

    const handleCancelPoint = () => {
        if (pendingFeatureRef.current) {
            beneficiaryVectorSource.current.removeFeature(pendingFeatureRef.current);
        }
        // Also fully clear the draw source (should only contain stale features)
        vectorSource.current.clear();
        beneficiaryVectorSource.current.changed();
        vectorSource.current.changed();
        setPendingPoint(null);
        setOutsideTarget(null);
        pendingFeatureRef.current = null;
        if (pendingBeneficiarioId && map && !drawInteractionRef.current) {
            addDrawInteraction('Point');
        }
    };

    const toggleLayer = (key) => {
        setLayerVisibility(prev => {
            const next = { ...prev, [key]: !prev[key] };
            if (key === 'arba') arbaLayerRef.current?.setVisible(next.arba);
            else if (key === 'beneficiarios') layersRef.current.beneficiarios?.setVisible(next.beneficiarios);
            else barrioLayersRef.current[key]?.setVisible(next[key]);
            return next;
        });
    };

    const handleDrawTypeChange = (event) => {
        addDrawInteraction(event.target.value);
    };

    const handleDeleteItemFromList = (featureIdToDelete) => {
        const featureToDelete = vectorSource.current.getFeatureById(featureIdToDelete);
        deleteFeature(featureIdToDelete);
        if (featureToDelete) {
            vectorSource.current.removeFeature(featureToDelete);
        }
        highlightSource.clear();
        setSelectedWKTIndex(null);
        if (selectInteractionRef.current) {
            selectInteractionRef.current.getFeatures().clear();
        }
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const geojsonFeatures = new GeoJSON().readFeatures(e.target.result, {
                    featureProjection: 'EPSG:4326',
                    dataProjection: 'EPSG:4326',
                });
                const newFeatureDataFromGeoJSON = geojsonFeatures.map(feature => {
                    const uuid = crypto.randomUUID();
                    feature.setId(uuid);
                    const wktFmt = new WKT();
                    const featureWKT4326 = wktFmt.writeFeature(feature);
                    return { id: uuid, wkt: featureWKT4326, feature };
                });
                vectorSource.current.clear();
                newFeatureDataFromGeoJSON.forEach(item => vectorSource.current.addFeature(item.feature));
                vectorSource.current.changed();
            };
            reader.readAsText(file);
        }
    };

    const handleExportGeoJSON = () => {
        const features = vectorSource.current.getFeatures();
        return new GeoJSON().writeFeatures(features, {
            featureProjection: 'EPSG:4326',
            dataProjection: 'EPSG:4326',
        });
    };

    const handleCloseModal = () => {
        onClose(handleExportGeoJSON());
    };

    const renderWKTList = () => {
        return drawnFeatures.map((featureItem, index) => (
            <li
                key={featureItem.id}
                data-feature-id={featureItem.id}
                className={`my-2 p-3 rounded-md border-2 transition-colors duration-200 list-none cursor-pointer 
                    ${selectedWKTIndex === index
                        ? 'bg-indigo-100 border-indigo-500 text-indigo-800 shadow-md'
                        : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'}`}
                onClick={() => {
                    setSelectedWKTIndex(index);
                    const selectedItem = featureData[index];
                    if (selectedItem && selectedItem.feature) {
                        highlightSource.clear();
                        highlightSource.addFeature(selectedItem.feature);
                        if (selectInteractionRef.current) {
                            selectInteractionRef.current.getFeatures().clear();
                            selectInteractionRef.current.getFeatures().push(selectedItem.feature);
                        }
                    }
                }}
            >
                <span className="font-mono text-sm">ID: {featureItem.ol_uid} - {featureItem.wkt.substring(0, 50)}...</span>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteItemFromList(featureItem.id);
                    }}
                    className="ml-2 p-1 bg-red-500 text-white rounded-md text-xs hover:bg-red-700"
                >
                    Eliminar
                </button>
            </li>
        ));
    };

    const handleFeatureSelectOnMap = (selectedFeatures) => {
        if (selectedFeatures && selectedFeatures.length > 0) {
            const selectedFeature = selectedFeatures[0];
            const selectedFeatureId = selectedFeature.getId();
            if (!selectedFeatureId) return;

            const selectedIndex = featureData.findIndex(item => {
                if (!item || !item.feature) return false;
                const itemFeatureId = item.feature.getId();
                return itemFeatureId && itemFeatureId.toString() === selectedFeatureId.toString();
            });

            if (selectedIndex !== -1) {
                setSelectedWKTIndex(selectedIndex);
                highlightSource.clear();
                highlightSource.addFeature(selectedFeature);
                const listItem = document.querySelector(`li[data-feature-id="${selectedFeatureId}"]`);
                if (listItem) listItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            } else {
                setSelectedWKTIndex(null);
                highlightSource.clear();
            }
        } else {
            setSelectedWKTIndex(null);
            highlightSource.clear();
            if (selectInteractionRef.current) {
                selectInteractionRef.current.getFeatures().clear();
            }
        }
    };

    return (
        <div className="h-full flex flex-col">
            <div ref={mapRef} className="w-full flex-grow border border-gray-300 relative">

                <PanelDebug barrioCounts={barrioCounts} debugInfo={debugInfo} />

                <div className="absolute top-2 left-2 flex flex-col gap-2 z-10 max-w-[220px]">
                    <PanelCapas
                        layerVisibility={layerVisibility}
                        toggleLayer={toggleLayer}
                        hasRenabapData={hasRenabapData}
                        hasRppvapData={hasRppvapData}
                        showBeneficiarios={true}
                        showArba={true}
                        hasRenabapTargets={hasRenabapTargets.current}
                        hasRppvapTargets={hasRppvapTargets.current}
                    />

                    <label className="flex items-center gap-2 bg-white bg-opacity-95 rounded-lg shadow-lg px-3 py-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={showNonTargetOverlay}
                            onChange={(e) => handleShowNonTargetOverlay(e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-xs text-gray-700">Mostrar info</span>
                    </label>

                    {false && !pendingBeneficiarioId && !geoEditMode && (
                        <div className="bg-white bg-opacity-95 rounded-lg shadow-lg p-2">
                            <select
                                onChange={handleDrawTypeChange}
                                value={selectedDrawType}
                                className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                            >
                                <option value="none">Seleccionar Dibujo</option>
                                <option value="Point">Punto</option>
                                <option value="LineString">Línea</option>
                                <option value="Polygon">Polígono</option>
                            </select>
                        </div>
                    )}
                </div>

                <PanelLeyenda
                    hasRenabapData={hasRenabapData}
                    hasRppvapData={hasRppvapData}
                    showBeneficiario={true}
                    showAsignandoPunto={!!pendingBeneficiarioId}
                    hasRenabapTargets={hasRenabapTargets.current}
                    hasRppvapTargets={hasRppvapTargets.current}
                />

                {pendingPoint && (
                    <div className="absolute bottom-2 right-2 z-10 flex flex-col items-end gap-2 pointer-events-none">
                        <div className={`bg-white bg-opacity-95 rounded-lg shadow-lg px-4 py-3 text-sm max-w-md w-full pointer-events-auto ${outsideTarget ? 'border-2 border-orange-400' : ''}`}>
                            {outsideTarget ? (
                                <>
                                    {outsideTarget.inBarrios ? (
                                        <>
                                            <p className="font-semibold text-orange-600 mb-1">Se agregarán como barrios seleccionados</p>
                                            {outsideTarget.inBarrios.map((b, i) => (
                                                <p key={i} className="text-xs text-gray-600 mb-1">{b.get('nombre')} ({b.get('tipo')})</p>
                                            ))}
                                        </>
                                    ) : (
                                        <p className="font-semibold text-orange-600 mb-1">Fuera de todo barrio</p>
                                    )}
                                    <p className="text-xs text-gray-400">{pendingPoint.lat.toFixed(6)}, {pendingPoint.lng.toFixed(6)}</p>
                                </>
                            ) : (
                                <>
                                    <p className="font-semibold text-gray-800 mb-1">¿Confirmar ubicación?</p>
                                    <p className="text-xs text-gray-500">{pendingPoint.lat.toFixed(6)}, {pendingPoint.lng.toFixed(6)}</p>
                                </>
                            )}
                        </div>
                        <div className="flex items-center gap-2 pointer-events-auto">
                            <button
                                onClick={handleCancelPoint}
                                className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 bg-white bg-opacity-90 rounded border border-gray-300 hover:border-gray-400 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleConfirmPoint}
                                className="px-6 py-2 rounded-lg text-white font-semibold shadow-lg text-sm bg-green-600 hover:bg-green-700 transition-colors"
                            >
                                Confirmar
                            </button>
                        </div>
                    </div>
                )}

                {drawnFeatures.length > 0 && (
                    <div className="absolute top-2 right-2 w-64 bg-white bg-opacity-90 rounded-lg shadow-lg z-10 overflow-hidden">
                        <div className="bg-gray-100 p-2 border-b border-gray-200">
                            <h3 className="font-semibold text-sm">Geometrías Dibujadas</h3>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                            <ul className="divide-y divide-gray-200">{renderWKTList()}</ul>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});

export default MapView;
